/* eslint-disable consistent-return */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import { IconArrowDown07 } from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface IProps {
  children?: React.ReactNode;
  className?: string;
  /** 이 줄 수를 넘기면 접어두고 "펼치기" 버튼을 노출한다. 안 넘기면 접지 않는다 */
  foldLine?: number;
  /** 올려 두는 배경 — 접기 버튼과 그라데이션 색이 갈린다 */
  bgType?: 'white' | 'black';
  colGap?: number;
  rowGap?: number;
}

/**
 * 탭을 가로 스크롤/스와이프 없이 여러 줄로 흘리고, 지정한 줄 수까지만 보여주는 리스트.
 * 스와이프가 없는 PC 메뉴바(CategoryMenuBar / Menu2LineBar)가 공용으로 쓴다.
 * 아이템 스킨은 감싸는 쪽(.cm-type11 등)이 잡고, 여기서는 배치와 접기만 맡는다.
 */
const WrapMenuBar = ({
  children,
  className,
  foldLine,
  bgType = 'white',
  colGap = 6,
  rowGap = 8,
}: IProps) => {
  const listRef = useRef<HTMLDivElement | null>(null);

  /** 접힌 상태에서 보여줄 높이(px). null 이면 접을 만큼 길지 않다는 뜻 */
  const [foldHeight, setFoldHeight] = useState<number | null>(null);
  const [isFolded, setIsFolded] = useState(true);

  /**
   * 접었을 때 보여줄 높이를 잰다.
   * 아이템의 offsetTop 이 몇 종류인지가 곧 줄 수다.
   */
  useEffect(() => {
    if (!foldLine) return;

    let rafId = 0;

    const measure = () => {
      const list = listRef.current;
      if (!list) return;

      const items = Array.from(list.children) as HTMLElement[];
      if (!items.length) return;

      const rowTops = Array.from(new Set(items.map(v => v.offsetTop))).sort(
        (a, b) => a - b
      );

      // 제한 줄 수 안에 다 들어가면 접지 않는다 (버튼도 안 나온다)
      if (rowTops.length <= foldLine) {
        setFoldHeight(null);
        return;
      }

      // 마지막 노출 줄의 하단 = 그 다음 줄의 시작점 - row-gap
      setFoldHeight(rowTops[foldLine] - rowTops[0] - rowGap);
    };

    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [foldLine, rowGap, children]);

  const handleClickFold = useCallback(() => {
    setIsFolded(false);
  }, []);

  // 펼친 뒤에는 버튼을 다시 노출하지 않는다
  const showFoldBtn = !!foldLine && foldHeight !== null && isFolded;

  return (
    <S.WrapMenuBar
      className={classNameBind([
        'wrap-menubar',
        showFoldBtn ? 'is-folded' : '',
        className || '',
      ])}
      $bgType={bgType}
      $colGap={colGap}
      $rowGap={rowGap}
      $foldHeight={foldHeight}
    >
      <div className="wrap-menubar-list" ref={listRef}>
        {children}
      </div>

      {showFoldBtn && (
        <div className="btn-fold-wrap">
          <Button
            className="btn-fold"
            label="탭 전체 펼치기"
            ariaExpanded={false}
            onClick={handleClickFold}
          >
            <IconArrowDown07 />
          </Button>
        </div>
      )}
    </S.WrapMenuBar>
  );
};

export default memo(WrapMenuBar);
